// 虚拟滚动消息列表组件

import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Message } from '../types';
import { MessageItem } from './MessageItem';

interface VirtualMessageListProps {
  messages: Message[];
  onRetry?: (messageId: string) => void;
  onMarkAsRead?: (messageKey: string) => void;
  itemHeight?: number; // 预估消息高度
  overscan?: number; // 预渲染的额外项目数
  hasMoreHistory?: boolean; // 是否有更多历史消息
  isLoadingHistory?: boolean; // 是否正在加载历史消息
  onLoadMoreHistory?: () => void; // 加载更多历史消息的回调
  isVisible?: boolean; // 聊天窗口是否可见
}

export const VirtualMessageList = forwardRef<any, VirtualMessageListProps>(({
  messages,
  onRetry,
  onMarkAsRead,
  itemHeight = 80, // 默认消息高度
  overscan = 5, // 预渲染5个额外项目
  hasMoreHistory = false,
  isLoadingHistory = false,
  onLoadMoreHistory,
  isVisible = true // 默认可见
}, ref) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [previousMessageCount, setPreviousMessageCount] = useState(0);
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 计算可见范围
  const visibleRange = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      messages.length - 1,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );
    
    return { startIndex, endIndex };
  }, [scrollTop, containerHeight, itemHeight, overscan, messages.length]);

  // 调试信息 - 只在聊天窗口可见时输出
  useEffect(() => {
    if (isVisible) {
      console.log('虚拟滚动状态:', {
        messagesLength: messages.length,
        scrollTop,
        containerHeight,
        visibleRange,
        hasMoreHistory,
        isLoadingHistory
      });
    }
  }, [messages.length, scrollTop, containerHeight, visibleRange, hasMoreHistory, isLoadingHistory, isVisible]);

  // 可见消息
  const visibleMessages = useMemo(() => {
    return messages.slice(visibleRange.startIndex, visibleRange.endIndex + 1);
  }, [messages, visibleRange]);

  // 总高度
  const totalHeight = messages.length * itemHeight;

  // 偏移量
  const offsetY = visibleRange.startIndex * itemHeight;

  // 滚动处理
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop: currentScrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setScrollTop(currentScrollTop);
    
    // 检测用户是否在滚动（不是程序触发的滚动）
    const isAtTop = currentScrollTop <= 5;
    const isAtBottom = currentScrollTop >= scrollHeight - clientHeight - 10;
    
    // 如果用户滚动到顶部，标记为用户滚动状态
    if (isAtTop) {
      setIsUserScrolling(true);
    }
    
    // 如果用户滚动到底部，取消用户滚动状态
    if (isAtBottom) {
      setIsUserScrolling(false);
    }
    
    // 检测是否滚动到顶部（加载历史消息）
    if (isAtTop && hasMoreHistory && !isLoadingHistory && onLoadMoreHistory) {
      console.log('滚动到顶部，开始加载历史消息', {
        scrollTop: currentScrollTop,
        hasMoreHistory,
        isLoadingHistory,
        messagesLength: messages.length,
        totalHeight,
        containerHeight,
        isUserScrolling
      });
      
      // 标记正在加载历史消息
      setIsLoadingMore(true);
      onLoadMoreHistory();
    }
  }, [hasMoreHistory, isLoadingHistory, onLoadMoreHistory, messages.length, totalHeight, containerHeight]);

  // 容器尺寸变化处理
  useEffect(() => {
    const updateContainerHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    updateContainerHeight();
    
    const resizeObserver = new ResizeObserver(updateContainerHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      console.log('滚动到底部 - 容器信息:', {
        scrollHeight,
        clientHeight,
        currentScrollTop: container.scrollTop,
        maxScrollTop: scrollHeight - clientHeight
      });
      
      // 使用多种方法确保滚动成功
      const scrollToMax = () => {
        container.scrollTop = scrollHeight;
        console.log('滚动完成，新的 scrollTop:', container.scrollTop);
      };
      
      // 立即执行一次
      scrollToMax();
      
      // 使用 requestAnimationFrame 确保在下一帧执行
      requestAnimationFrame(() => {
        scrollToMax();
        
        // 再次使用 setTimeout 确保滚动成功
        setTimeout(() => {
          scrollToMax();
        }, 50);
      });
    }
  }, []);

  // 初始加载时滚动到底部 - 只在首次打开且没有用户交互时执行
  useEffect(() => {
    // 只在以下条件都满足时才滚动：
    // 1. 有消息
    // 2. 聊天窗口可见
    // 3. 不是正在加载历史消息
    // 4. 用户没有在滚动（避免与用户操作冲突）
    // 5. 容器有滚动条（内容超出容器高度）
    // 6. 用户还没有进行过任何滚动操作
    if (messages.length > 0 && isVisible && !isLoadingMore && !isUserScrolling && containerRef.current) {
      const container = containerRef.current;
      const hasScrollbar = container.scrollHeight > container.clientHeight;
      
      console.log('检查是否需要初始滚动', {
        messagesLength: messages.length,
        isVisible,
        isLoadingMore,
        isUserScrolling,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
        hasScrollbar,
        currentScrollTop: container.scrollTop
      });
      
      // 只有在有滚动条且用户还没有进行过滚动操作时才执行滚动
      if (hasScrollbar && container.scrollTop === 0) {
        console.log('首次打开聊天窗口，自动滚动到底部');
        
        // 延迟滚动，确保DOM完全渲染
        setTimeout(() => {
          scrollToBottom();
        }, 300);
      } else {
        console.log('用户已进行过滚动操作或已在底部，不自动滚动');
      }
    }
  }, [messages.length, isVisible, scrollToBottom, isLoadingMore, isUserScrolling]);


  // 处理历史消息加载后的滚动位置保持
  useEffect(() => {
    if (isLoadingMore && messages.length > previousMessageCount) {
      // 历史消息加载完成，保持滚动位置
      const newMessagesCount = messages.length - previousMessageCount;
      const newContentHeight = newMessagesCount * itemHeight;
      
      console.log('历史消息加载完成，保持滚动位置', {
        newMessagesCount,
        newContentHeight,
        isUserScrolling,
        currentScrollTop: containerRef.current?.scrollTop
      });
      
      if (containerRef.current) {
        // 调整滚动位置，保持用户当前看到的内容位置
        const currentScrollTop = containerRef.current.scrollTop;
        containerRef.current.scrollTop = currentScrollTop + newContentHeight;
        
        console.log('滚动位置已调整', {
          oldScrollTop: currentScrollTop,
          newScrollTop: containerRef.current.scrollTop
        });
      }
      
      setIsLoadingMore(false);
    }
    setPreviousMessageCount(messages.length);
  }, [messages.length, previousMessageCount, itemHeight, isLoadingMore]);

  // 暴露滚动到底部方法给父组件
  useImperativeHandle(ref, () => ({
    scrollToBottom
  }), [scrollToBottom]);

  // 普通模式下的滚动位置保持逻辑
  useEffect(() => {
    if (messages.length <= 50 && isLoadingMore && messages.length > previousMessageCount) {
      // 普通模式下历史消息加载完成，保持滚动位置
      const newMessagesCount = messages.length - previousMessageCount;
      
      if (containerRef.current) {
        // 获取当前滚动位置
        const currentScrollTop = containerRef.current.scrollTop;
        const currentScrollHeight = containerRef.current.scrollHeight;
        
        // 延迟执行，确保DOM已更新
        setTimeout(() => {
          if (containerRef.current) {
            const newScrollHeight = containerRef.current.scrollHeight;
            const heightDifference = newScrollHeight - currentScrollHeight;
            
            // 调整滚动位置，保持用户当前看到的内容位置
            containerRef.current.scrollTop = currentScrollTop + heightDifference;
          }
        }, 50);
      }
      
      setIsLoadingMore(false);
    }
  }, [messages.length, previousMessageCount, isLoadingMore]);

  // 如果消息数量较少，直接渲染所有消息
  if (messages.length <= 50) {
    return (
      <div 
        ref={containerRef}
        className="chat-widget__messages"
        onScroll={handleScroll}
        style={{ height: '100%', overflow: 'auto' }}
      >
        {/* 历史消息加载指示器 */}
        {isLoadingHistory && hasMoreHistory && (
          <div className="chat-widget__loading-history">
            <div className="chat-widget__loading-spinner">
              <div className="chat-widget__spinner"></div>
            </div>
            <span>loading...</span>
          </div>
        )}
        
        {messages.map((message) => (
          <MessageItem
            key={message.id}
            message={message}
            onRetry={onRetry || (() => {})}
            onMarkAsRead={onMarkAsRead}
          />
        ))}
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="chat-widget__messages"
      onScroll={handleScroll}
      style={{ height: '100%', overflow: 'auto' }}
    >
      {/* 历史消息加载指示器 */}
      {isLoadingHistory && hasMoreHistory && (
        <div className="chat-widget__loading-history">
          <div className="chat-widget__loading-spinner">
            <div className="chat-widget__spinner"></div>
          </div>
          <span>loading...</span>
        </div>
      )}
      
      {/* 虚拟滚动容器 */}
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div
          style={{
            transform: `translateY(${offsetY}px)`,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0
          }}
        >
          {visibleMessages.map((message, index) => (
            <div
              key={message.id}
              style={{ height: itemHeight }}
            >
              <MessageItem
                message={message}
                onRetry={onRetry || (() => {})}
                onMarkAsRead={onMarkAsRead}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

VirtualMessageList.displayName = 'VirtualMessageList';

export default VirtualMessageList;
